import React, { memo } from 'react';
import {
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY } from '../../theme/sapDesignSystem';

// Common field styling
const commonFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: SAP_COLORS.sapGray3,
    },
    '&:hover fieldset': {
      borderColor: SAP_COLORS.sapBlue,
    },
    '&.Mui-focused fieldset': {
      borderColor: SAP_COLORS.sapBlue,
    },
  },
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray6,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
  '& .MuiInputBase-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
};

// Styled components
const StyledTextField = styled(TextField)(commonFieldStyles);
const StyledAutocomplete = styled(Autocomplete)({
  width: '100%',
  ...commonFieldStyles,
});
const StyledSelect = styled(FormControl)({
  minWidth: '200px',
  ...commonFieldStyles,
});

// Base field props interface
export interface BaseFieldProps {
  label: string;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
  sx?: any;
}

// Text field props
export interface TextFieldProps extends BaseFieldProps {
  type: 'text';
  multiline?: boolean;
  rows?: number;
}

// Autocomplete field props
export interface AutocompleteFieldProps extends BaseFieldProps {
  type: 'autocomplete';
  options: Array<{ label: string; value: any; [key: string]: any }>;
  getOptionLabel?: (option: any) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  loading?: boolean;
  groupBy?: (option: any) => string;
  noOptionsText?: string;
  multiple?: boolean;
}

// Select field props
export interface SelectFieldProps extends BaseFieldProps {
  type: 'select';
  options: Array<{ label: string; value: any; [key: string]: any }>;
  emptyOption?: string;
}

// Date field props
export interface DateFieldProps extends BaseFieldProps {
  type: 'date';
  format?: string;
  views?: Array<'year' | 'month' | 'day'>;
}

// Union type for all field props
export type FilterFieldProps = 
  | TextFieldProps 
  | AutocompleteFieldProps 
  | SelectFieldProps 
  | DateFieldProps;

// Generic FilterField component
export const FilterField = memo<FilterFieldProps>((props) => {
  const { 
    label, 
    value, 
    onChange, 
    disabled = false, 
    required = false, 
    placeholder, 
    size = 'small',
    sx = {},
    ...fieldProps 
  } = props;

  const commonProps = {
    label,
    value: value || (fieldProps.type === 'autocomplete' && fieldProps.multiple ? [] : ''),
    onChange,
    disabled,
    required,
    placeholder,
    size,
    sx: { ...commonFieldStyles, ...sx },
  };

  switch (fieldProps.type) {
    case 'text':
      return (
        <StyledTextField
          {...commonProps}
          multiline={fieldProps.multiline}
          rows={fieldProps.rows}
        />
      );

    case 'autocomplete':
      return (
        <StyledAutocomplete
          options={fieldProps.options}
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
          getOptionLabel={fieldProps.getOptionLabel || ((option: any) => option?.label || option || '')}
          isOptionEqualToValue={fieldProps.isOptionEqualToValue || ((option: any, value: any) => option?.value === value?.value)}
          loading={fieldProps.loading}
          groupBy={fieldProps.groupBy}
          noOptionsText={fieldProps.noOptionsText || 'No options'}
          multiple={fieldProps.multiple}
          disabled={disabled}
          size={size}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label={label}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              size={size}
              sx={{ ...commonFieldStyles, ...sx }}
            />
          )}
          sx={{ ...commonFieldStyles, ...sx }}
        />
      );

    case 'select':
      return (
        <StyledSelect size={size} required={required} disabled={disabled}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label={label}
            disabled={disabled}
            sx={{ ...commonFieldStyles, ...sx }}
          >
            {fieldProps.emptyOption && (
              <MenuItem value="">
                <em>{fieldProps.emptyOption}</em>
              </MenuItem>
            )}
            {fieldProps.options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </StyledSelect>
      );

    case 'date':
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={label}
            value={value}
            onChange={onChange}
            format={fieldProps.format || 'MM/dd/yyyy'}
            views={fieldProps.views}
            disabled={disabled}
            slotProps={{
              textField: {
                size,
                placeholder,
                required,
                disabled,
                sx: { ...commonFieldStyles, ...sx },
              },
            }}
          />
        </LocalizationProvider>
      );

    default:
      return null;
  }
});

FilterField.displayName = 'FilterField';

// Helper function to create field configurations
export const createTextField = (
  label: string,
  value: string,
  onChange: (value: string) => void,
  options: Partial<TextFieldProps> = {}
): TextFieldProps => ({
  type: 'text',
  label,
  value,
  onChange,
  ...options,
});

export const createAutocompleteField = (
  label: string,
  value: any,
  onChange: (value: any) => void,
  options: Array<{ label: string; value: any; [key: string]: any }>,
  fieldOptions: Partial<AutocompleteFieldProps> = {}
): AutocompleteFieldProps => ({
  type: 'autocomplete',
  label,
  value,
  onChange,
  options,
  ...fieldOptions,
});

export const createSelectField = (
  label: string,
  value: any,
  onChange: (value: any) => void,
  options: Array<{ label: string; value: any; [key: string]: any }>,
  fieldOptions: Partial<SelectFieldProps> = {}
): SelectFieldProps => ({
  type: 'select',
  label,
  value,
  onChange,
  options,
  ...fieldOptions,
});

export const createDateField = (
  label: string,
  value: any,
  onChange: (value: any) => void,
  fieldOptions: Partial<DateFieldProps> = {}
): DateFieldProps => ({
  type: 'date',
  label,
  value,
  onChange,
  ...fieldOptions,
});

// Export individual field components for direct use
export { FilterField as TextFilterField };
export { FilterField as AutocompleteFilterField };
export { FilterField as SelectFilterField };
export { FilterField as DateFilterField };

export default FilterField;
